export const TRUNG_THU_EMPLOYEE_DISCOUNT_LIMIT = 1_000_000;
export const TRUNG_THU_EMPLOYEE_DISCOUNT_PERCENT = 15;
export const TRUNG_THU_FLASH_SALE_DISCOUNT_PERCENT = 50;
export const TRUNG_THU_FLASH_SALE_ENABLED = ["on", "true", "1"].includes(
  String(process.env.REACT_APP_FLASHSALE || "on").trim().toLowerCase(),
);

export const TRUNG_THU_SPECIAL_DISCOUNT_PRODUCT_CODES = Object.freeze([
  "BTT004-A",
  "BTT012-A",
  "BTT003-A",
  "BTT002-A",
  "BTT001-A",
  "BTT007-A",
  "BTT006-A",
  "BTT010-A",
  "BTT011-A",
  "BTT004",
  "BTT003",
  "BTT001",
  "BTT009-A",
  "BTT006-A1",
]);

const SPECIAL_PRODUCT_CODES = new Set(TRUNG_THU_SPECIAL_DISCOUNT_PRODUCT_CODES);

const normalizeProductCode = (value) => String(value ?? "").trim().toUpperCase();

export const TRUNG_THU_NEGATIVE_PRICE_PRODUCT_CODES = Object.freeze(
  [
    ...new Set(
      String(process.env.REACT_APP_TRUNG_THU_NEGATIVE_PRICE_PRODUCT_CODES || "")
        .split(",")
        .map(normalizeProductCode)
        .filter(Boolean),
    ),
  ],
);

const NEGATIVE_PRICE_PRODUCT_CODES = new Set(
  TRUNG_THU_NEGATIVE_PRICE_PRODUCT_CODES,
);

export const isTrungThuNegativePriceProduct = (productCode) =>
  NEGATIVE_PRICE_PRODUCT_CODES.has(normalizeProductCode(productCode));

const getTrungThuUnitPrice = (item) => {
  const parsedPrice = Number(item?.price);
  const unitPrice = Number.isFinite(parsedPrice) ? parsedPrice : 0;

  if (isTrungThuNegativePriceProduct(item?.id)) {
    return unitPrice === 0 ? 0 : -Math.abs(unitPrice);
  }

  return unitPrice < 0 ? 0 : unitPrice;
};

export const isTrungThuSpecialDiscountProduct = (productCode) =>
  SPECIAL_PRODUCT_CODES.has(normalizeProductCode(productCode));

export const getTrungThuAutomaticDiscountPercent = (productCode, subtotal) => {
  const safeSubtotal = Math.max(0, Number(subtotal) || 0);

  if (isTrungThuSpecialDiscountProduct(productCode)) {
    if (safeSubtotal >= 200_000_000) return 28;
    if (safeSubtotal >= 95_000_000) return 25;
    if (safeSubtotal >= 85_000_000) return 23;
    if (safeSubtotal >= 65_000_000) return 20;
    if (safeSubtotal >= 45_000_000) return 17;
    if (safeSubtotal >= 35_000_000) return 15;
    if (safeSubtotal >= 30_000_000) return 12;
    if (safeSubtotal >= 16_000_000) return 10;
    if (safeSubtotal >= 14_000_000) return 7;
    if (safeSubtotal >= 5_000_000) return 5;
    return 0;
  }

  if (safeSubtotal > 100_000_000) return 28;
  if (safeSubtotal > 70_000_000) return 25;
  if (safeSubtotal > 50_000_000) return 23;
  if (safeSubtotal > 30_000_000) return 20;
  if (safeSubtotal > 20_000_000) return 18;
  if (safeSubtotal > 10_000_000) return 16;
  if (safeSubtotal > 5_000_000) return 14;
  if (safeSubtotal > 3_000_000) return 9;
  if (safeSubtotal > 1_500_000) return 7;
  return 0;
};

export const calculateTrungThuDiscount = (
  items,
  {
    employeeDiscountRequested = false,
    flashSaleEnabled = TRUNG_THU_FLASH_SALE_ENABLED,
  } = {},
) => {
  const safeItems = Array.isArray(items) ? items : [];
  const groupSubtotals = safeItems.reduce(
    (totals, item) => {
      const lineSubtotal =
        getTrungThuUnitPrice(item) *
        Math.max(0, Number(item?.quantity) || 0);
      const group = isTrungThuSpecialDiscountProduct(item?.id)
        ? "bonpas"
        : "dongTien";

      totals[group] += lineSubtotal;
      return totals;
    },
    { bonpas: 0, dongTien: 0 },
  );
  const subtotal = groupSubtotals.bonpas + groupSubtotals.dongTien;
  const employeeDiscountEligible =
    !flashSaleEnabled &&
    subtotal > 0 &&
    subtotal < TRUNG_THU_EMPLOYEE_DISCOUNT_LIMIT;
  const employeeDiscountApplied =
    Boolean(employeeDiscountRequested) && employeeDiscountEligible;
  const groupDiscountPercents = {
    bonpas:
      groupSubtotals.bonpas > 0
        ? flashSaleEnabled
          ? TRUNG_THU_FLASH_SALE_DISCOUNT_PERCENT
          : employeeDiscountApplied
          ? TRUNG_THU_EMPLOYEE_DISCOUNT_PERCENT
          : getTrungThuAutomaticDiscountPercent(
              TRUNG_THU_SPECIAL_DISCOUNT_PRODUCT_CODES[0],
              groupSubtotals.bonpas,
            )
        : 0,
    dongTien:
      groupSubtotals.dongTien > 0
        ? flashSaleEnabled
          ? TRUNG_THU_FLASH_SALE_DISCOUNT_PERCENT
          : employeeDiscountApplied
          ? TRUNG_THU_EMPLOYEE_DISCOUNT_PERCENT
          : getTrungThuAutomaticDiscountPercent("", groupSubtotals.dongTien)
        : 0,
  };

  const discountedItems = safeItems.map((item) => {
    const unitPrice = getTrungThuUnitPrice(item);
    const lineSubtotal =
      unitPrice *
      Math.max(0, Number(item?.quantity) || 0);
    const isBonpas = isTrungThuSpecialDiscountProduct(item?.id);
    const discountGroup = isBonpas ? "bonpas" : "dongTien";
    const discountPercent =
      lineSubtotal < 0 ? 0 : groupDiscountPercents[discountGroup];
    const discountAmount = lineSubtotal * (discountPercent / 100);

    return {
      ...item,
      price: unitPrice,
      discountPercent,
      discountGroup,
      discountAmount,
      lineSubtotal,
      lineTotal: lineSubtotal - discountAmount,
    };
  });
  const discountAmount = discountedItems.reduce(
    (sum, item) => sum + item.discountAmount,
    0,
  );
  const effectiveDiscountPercent =
    subtotal > 0 ? (discountAmount / subtotal) * 100 : 0;
  const appliedDiscountPercents = [
    ...new Set(
      discountedItems
        .map((item) => item.discountPercent)
        .filter((percent) => percent > 0),
    ),
  ].sort((left, right) => left - right);

  return {
    subtotal,
    groupSubtotals,
    groupDiscountPercents,
    discountAmount,
    effectiveDiscountPercent,
    flashSaleEnabled,
    employeeDiscountEligible,
    employeeDiscountApplied,
    appliedDiscountPercents,
    discountedItems,
  };
};
